import { useState } from "react";
import { useLang } from "../lib/useLang";
import { useLocation } from "../state/LocationContext";
import { useCart } from "../state/CartContext";
import { BRANCHES_SEED } from "../data/branches";
import { roadDistanceKm } from "../lib/pricing";
import { Money } from "./Money";
import { MapPicker } from "./MapPicker";
import type { Branch } from "../lib/types";

export function LocationSheet({ onDone }: { onDone?: () => void }) {
  const { t, pick } = useLang();
  const {
    branch,
    pin,
    area,
    mode,
    distanceKm,
    fulfillment,
    setBranch,
    setPin,
    setArea,
    setFulfillment,
    quote,
  } = useLocation();
  const isPickup = fulfillment === "pickup";
  const { subtotal } = useCart();
  const [geoError, setGeoError] = useState(false);
  const [tab, setTab] = useState<"area" | "pin">(mode === "pin" ? "pin" : "area");

  const branches = BRANCHES_SEED;
  const activeBranch = branch ?? branches[0];
  const q = quote(subtotal);

  function useMyLocation() {
    if (!navigator.geolocation) return setGeoError(true);
    navigator.geolocation.getCurrentPosition(
      (posn) => {
        const p = { lat: posn.coords.latitude, lng: posn.coords.longitude };
        const nearest = [...branches].sort(
          (a, b) => roadDistanceKm(a, p) - roadDistanceKm(b, p),
        )[0];
        setBranch(nearest);
        setPin(p);
      },
      () => setGeoError(true),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  function pickBranch(b: Branch) {
    setBranch(b);
    setArea(null);
    setPin(null);
  }

  return (
    <div className="card space-y-4 p-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900">{t("location.title")}</h2>
        <p className="text-sm text-black/55">{t("location.subtitle")}</p>
      </div>

      {/* Delivery vs Pickup */}
      <div className="flex gap-1 rounded-xl bg-black/5 p-1">
        <ModeTab active={!isPickup} onClick={() => setFulfillment("delivery")}>
          🛵 {t("location.delivery")}
        </ModeTab>
        <ModeTab active={isPickup} onClick={() => setFulfillment("pickup")}>
          🏪 {t("location.pickup")}
        </ModeTab>
      </div>

      {/* Branch selector */}
      <div className="flex gap-2">
        {branches.map((b) => {
          const selected = branch?.id === b.id;
          return (
            <button
              key={b.id}
              onClick={() => pickBranch(b)}
              className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                selected
                  ? "border-brand bg-brand text-white"
                  : "border-black/10 bg-white text-gray-700 hover:border-brand/40"
              }`}
            >
              {pick(b.name, b.nameAr)}
            </button>
          );
        })}
      </div>

      {isPickup && (
        <div className="rounded-xl bg-cream p-3 text-sm">
          <div className="font-semibold text-ink">
            🏪 {t("location.pickupFrom")} {pick(activeBranch.name, activeBranch.nameAr)}
          </div>
          {activeBranch.addressEn && (
            <p className="mt-1 text-black/55" dir="auto">
              {pick(activeBranch.addressEn, activeBranch.addressAr ?? "")}
            </p>
          )}
          <p className="mt-1 text-xs text-black/45">{t("location.pickupNote")}</p>
        </div>
      )}

      {/* Mode tabs: manual area vs map pin (delivery only) */}
      {!isPickup && (
        <div className="flex gap-1 rounded-xl bg-black/5 p-1">
          <ModeTab active={tab === "area"} onClick={() => setTab("area")}>
            📋 {t("location.byArea")}
          </ModeTab>
          <ModeTab active={tab === "pin"} onClick={() => setTab("pin")}>
            📍 {t("location.byPin")}
          </ModeTab>
        </div>
      )}

      {isPickup ? null : tab === "area" ? (
        <div>
          <p className="mb-2 text-xs font-semibold text-black/50">
            {t("location.selectArea")}
          </p>
          <div className="flex flex-wrap gap-2">
            {activeBranch.serviceAreas.map((a) => {
              const selected = area?.en === a.en && branch?.id === activeBranch.id;
              return (
                <button
                  key={a.en}
                  onClick={() => {
                    setBranch(activeBranch);
                    setArea(a);
                  }}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                    selected
                      ? "border-accent bg-accent text-white"
                      : "border-black/10 bg-white text-gray-700 hover:border-accent/50"
                  }`}
                >
                  {pick(a.en, a.ar)}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-outline" onClick={useMyLocation}>
              📍 {t("location.useMyLocation")}
            </button>
            <span className="text-xs text-black/45">{t("location.pinOnMap")}</span>
          </div>
          {geoError && (
            <p className="text-xs text-brand">{t("location.orPickArea")}</p>
          )}
          <MapPicker
            key={activeBranch.id}
            center={pin ?? { lat: activeBranch.lat, lng: activeBranch.lng }}
            pin={pin}
            branchPos={{ lat: activeBranch.lat, lng: activeBranch.lng }}
            onPick={(lat, lng) => {
              setBranch(activeBranch);
              setPin({ lat, lng });
            }}
          />
        </div>
      )}

      {/* Fee readout (delivery only) */}
      {!isPickup && branch && q && (
        <div className="rounded-xl bg-cream p-3 text-sm">
          {mode === "pin" && distanceKm != null && (
            <div className="flex items-center justify-between">
              <span className="text-black/60">
                {t("location.distanceLabel", {
                  branch: pick(branch.name, branch.nameAr),
                })}
              </span>
              <span className="font-semibold">
                {distanceKm.toFixed(1)} {t("common.km")}
              </span>
            </div>
          )}
          {mode === "area" && area && (
            <div className="flex items-center justify-between">
              <span className="text-black/60">{t("location.deliverTo")}</span>
              <span className="font-semibold">{pick(area.en, area.ar)}</span>
            </div>
          )}
          {q.deliverable ? (
            <>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-black/60">{t("location.deliveryFee")}</span>
                <Money value={q.deliveryFee} className="font-bold text-brand" />
              </div>
              <p className="mt-1 text-xs text-black/45">{t("location.feeVaries")}</p>
            </>
          ) : (
            <p className="mt-2 font-semibold text-brand">
              {t("location.outsideArea", {
                branch: pick(branch.name, branch.nameAr),
                max: branch.pricing.maxDistanceKm,
              })}
            </p>
          )}
        </div>
      )}

      {onDone && (
        <button
          className="btn-primary w-full"
          onClick={onDone}
          disabled={!branch || !q?.deliverable}
        >
          {t("location.confirm")}
        </button>
      )}
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg py-1.5 text-sm font-semibold transition ${
        active ? "bg-white text-brand shadow-sm" : "text-black/55"
      }`}
    >
      {children}
    </button>
  );
}
